/**
 * Expense Create Modal (T164)
 * Form with all fields + receipt file upload
 */

import { useState } from 'react';
import { X, Upload, Loader2 } from 'lucide-react';
import { useCreateExpense } from '../../hooks/useExpenses';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORIES = [
  { value: 'OFFICE_SUPPLIES', label: 'Office Supplies' },
  { value: 'TECHNOLOGY', label: 'Technology' },
  { value: 'SOFTWARE_SUBSCRIPTIONS', label: 'Software Subscriptions' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'PROFESSIONAL_SERVICES', label: 'Professional Services' },
  { value: 'PROFESSIONAL_DEVELOPMENT', label: 'Professional Development' },
  { value: 'RENT', label: 'Rent' },
  { value: 'UTILITIES', label: 'Utilities' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'MEALS', label: 'Meals' },
  { value: 'OTHER', label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: '', label: 'Select method (optional)' },
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'DEBIT_CARD', label: 'Debit Card' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHECK', label: 'Check' },
  { value: 'APPLE_PAY', label: 'Apple Pay' },
  { value: 'GOOGLE_PAY', label: 'Google Pay' },
  { value: 'OTHER', label: 'Other' },
];

export default function ExpenseCreateModal({ onClose, onSuccess }: Props) {
  const createExpense = useCreateExpense();

  const [form, setForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    category: 'OTHER',
    paymentMethod: '',
    description: '',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.amount || parseFloat(form.amount) <= 0) {
      errs.amount = 'Amount must be positive';
    }
    if (!form.date) {
      errs.date = 'Date is required';
    }
    if (!form.vendor.trim()) {
      errs.vendor = 'Vendor is required';
    }
    if (!form.description.trim()) {
      errs.description = 'Description is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    try {
      await createExpense.mutateAsync({
        amount: parseFloat(form.amount),
        date: new Date(form.date).toISOString(),
        vendor: form.vendor.trim(),
        category: form.category,
        paymentMethod: form.paymentMethod || undefined,
        description: form.description.trim(),
        notes: form.notes.trim() || undefined,
      });
      onSuccess();
    } catch (err: any) {
      setErrors({ submit: err.message || 'Failed to create expense' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Add Expense</h2>
          <button onClick={onClose} className="claude-button claude-button-ghost p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Amount + Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className={`w-full pl-8 pr-4 py-2 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                    errors.amount ? 'border-destructive' : 'border-border'
                  }`}
                  placeholder="0.00"
                />
              </div>
              {errors.amount && <p className="text-xs text-destructive mt-1">{errors.amount}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Date *</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className={`w-full px-3 py-2 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  errors.date ? 'border-destructive' : 'border-border'
                }`}
              />
              {errors.date && <p className="text-xs text-destructive mt-1">{errors.date}</p>}
            </div>
          </div>

          {/* Vendor */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Vendor *</label>
            <input
              type="text"
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              className={`w-full px-3 py-2 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                errors.vendor ? 'border-destructive' : 'border-border'
              }`}
              placeholder="e.g., Amazon, Office Depot"
            />
            {errors.vendor && <p className="text-xs text-destructive mt-1">{errors.vendor}</p>}
          </div>

          {/* Category + Payment Method */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Category *</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Payment Method</label>
              <select
                value={form.paymentMethod}
                onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Description *</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={`w-full px-3 py-2 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                errors.description ? 'border-destructive' : 'border-border'
              }`}
              placeholder="What was this expense for?"
            />
            {errors.description && (
              <p className="text-xs text-destructive mt-1">{errors.description}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              placeholder="Additional notes (optional)"
            />
          </div>

          {/* Receipt Upload placeholder — Azure Blob (T161) */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Receipt</label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer">
              <Upload className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Drag & drop or click to upload receipt
              </p>
              <p className="text-xs text-muted-foreground mt-1">PDF, PNG, JPG up to 10MB</p>
            </div>
          </div>

          {/* Error */}
          {errors.submit && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {errors.submit}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="claude-button claude-button-ghost">
              Cancel
            </button>
            <button
              type="submit"
              disabled={createExpense.isPending}
              className="claude-button claude-button-primary disabled:opacity-50"
            >
              {createExpense.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Expense'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
